import { useEffect, useMemo, useState } from 'react'
import { useAppSelector } from '@/app/hooks'
import { selectIsAuthenticated, selectUser } from '@/features/auth/authSlice'
import { showApiError } from '@/lib/utils/notifications'
import { fetchAboutContent } from '../services/helpAboutApi'
import type { UseHelpAboutContentResult } from '../types/hooks'
import {
  getCurrentRoleNameForHelp,
  resolveHelpAboutContent,
} from '../utils/helpAboutContent'

/**
 * Loads Help/About CMS HTML — Angular `help-about.component.ts` parity:
 * - GET `/api/about_content` on mount when authenticated
 * - Role from Redux or `localStorage.user` (`getSelectedCurrentUserRole`)
 * - Role-based field: applicant / PMC / staff
 * - API errors via `notify` equivalent (`showApiError`)
 * - Raw CMS HTML (no post-processing; matches `bypassSecurityTrustHtml`)
 */
export function useHelpAboutContent(): UseHelpAboutContentResult {
  const user = useAppSelector(selectUser)
  const isAuthenticated = useAppSelector(selectIsAuthenticated)
  const roleName = useMemo(() => getCurrentRoleNameForHelp(user), [user])

  const [contentLoading, setContentLoading] = useState(true)
  /** `null` = error or unauthenticated; string (may be empty) = loaded CMS HTML */
  const [htmlContent, setHtmlContent] = useState<string | null>(null)

  useEffect(() => {
    if (!isAuthenticated) {
      setContentLoading(false)
      setHtmlContent(null)
      return
    }

    let cancelled = false

    async function loadContent() {
      setContentLoading(true)
      try {
        const data = await fetchAboutContent()
        if (cancelled) return

        const content = resolveHelpAboutContent(data, roleName)
        setHtmlContent(content ?? '')
      } catch (error) {
        if (!cancelled) {
          showApiError(error)
          setHtmlContent(null)
        }
      } finally {
        if (!cancelled) setContentLoading(false)
      }
    }

    void loadContent()

    return () => {
      cancelled = true
    }
  }, [isAuthenticated, roleName])

  return {
    contentLoading: isAuthenticated && contentLoading,
    htmlContent,
    roleName,
  }
}
